// ProviderLabel.jsx
import React, { useState, useEffect } from "react";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";

const API_BASE = "https://hms.backend.hiragroupcloud.com/api";

const ProviderLabel = ({
  caseNumber,
  providerName,         // current display name
  onProviderChange,     // update parent
}) => {
  const [showModal, setShowModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currentProvider, setCurrentProvider] = useState(providerName || "");

  // Fetch current case data and set initial provider
  useEffect(() => {
    if (!caseNumber) return;

    const fetchCurrentCase = async () => {
      try {
        const caseRes = await fetch(`${API_BASE}/cases/cases/${caseNumber}`);
        if (!caseRes.ok) throw new Error("Case not found");
        const caseData = await caseRes.json();
        
        // Set current provider from case data
        if (caseData.provider_name) {
          setCurrentProvider(caseData.provider_name);
          onProviderChange?.(caseData.provider_name);
        }
      } catch (err) {
        console.error("Failed to fetch case:", err);
      }
    };

    fetchCurrentCase();
  }, [caseNumber, onProviderChange]);

  // Fetch contacts when modal opens
  useEffect(() => {
    if (!showModal || !caseNumber) return;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const caseRes = await fetch(`${API_BASE}/cases/cases/${caseNumber}`);
        if (!caseRes.ok) throw new Error("Case not found");
        const caseData = await caseRes.json();

        const accountId = caseData.account_holder?.toString().trim();
        if (!accountId) throw new Error("No account");

        const contactsRes = await fetch(`${API_BASE}/contacts/${accountId}`);
        if (!contactsRes.ok) throw new Error("Failed to load contacts");
        const { data } = await contactsRes.json();

        const contactsArray = Array.isArray(data) ? data : [];
        setContacts(contactsArray);

        // Auto-select current provider from contacts if available
        if (caseData.provider_name && contactsArray.length > 0) {
          const matchedContact = contactsArray.find(
            (c) => `${c.firstName} ${c.lastName}`.trim() === caseData.provider_name.trim()
          );
          setSelectedContact(matchedContact || null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showModal, caseNumber]);

  // Sync with current providerName prop
  useEffect(() => {
    if (providerName) {
      setCurrentProvider(providerName);
    }
  }, [providerName]);

  // Sync selected contact when contacts change
  useEffect(() => {
    if (!currentProvider || contacts.length === 0) {
      setSelectedContact(null);
      return;
    }
    
    const match = contacts.find(
      (c) => `${c.firstName} ${c.lastName}`.trim() === currentProvider.trim()
    );
    setSelectedContact(match || null);
  }, [currentProvider, contacts]);

  const formatName = () => {
    if (!currentProvider) return "No provider assigned";
    return currentProvider;
  };

  const handleSave = async () => {
    if (!selectedContact?._id) {
      alert("Please select a contact");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/cases/${caseNumber}/provider`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider_id: selectedContact._id }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Save failed");
      }

      const result = await res.json();
      const newProviderName = result.data.provider_name;
      
      // Update local state and parent
      setCurrentProvider(newProviderName);
      onProviderChange?.(newProviderName);
      setShowModal(false);
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ fontWeight: "bold", fontSize: "18px" }}>
        Provider:
        <button
          onClick={() => setShowModal(true)}
          style={{
            marginLeft: "10px",
            backgroundColor: "#004aad",
            color: "white",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          <HiOutlineArrowsRightLeft />
        </button>
      </p>
      <h5>{formatName()}</h5>

      {showModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: "white", padding: "24px", borderRadius: "8px",
            width: "520px", maxHeight: "80vh", overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }}>
            <h4 style={{ marginTop: 0 }}>Change Provider</h4>

            {loading && <p>Loading contacts…</p>}
            {error && <p style={{ color: "red" }}>{error}</p>}

            {!loading && !error && contacts.length > 0 && (
              <>
                <label style={{ display: "block", marginBottom: "8px" }}>
                  Select Contact
                </label>
                <select
                  value={selectedContact ? `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() : ""}
                  onChange={(e) => {
                    const chosen = contacts.find(
                      (c) => `${c.firstName} ${c.lastName}`.trim() === e.target.value
                    );
                    setSelectedContact(chosen || null);
                  }}
                  style={{
                    width: "100%", padding: "10px", borderRadius: "4px",
                    border: "1px solid #ccc", marginBottom: "20px", fontSize: "16px"
                  }}
                >
                  <option value="">-- Select a contact --</option>
                  {contacts.map((c) => {
                    const name = `${c.firstName || ""} ${c.lastName || ""}`.trim();
                    return (
                      <option key={c._id} value={name}>
                        {name} {c.role ? `(${c.role})` : ""}
                      </option>
                    );
                  })}
                </select>
                
                {selectedContact && (
                  <div style={{ 
                    padding: "10px", 
                    backgroundColor: "#f8f9fa", 
                    borderRadius: "4px",
                    marginBottom: "15px"
                  }}>
                    <strong>Selected:</strong> {`${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim()}
                  </div>
                )}
              </>
            )}

            {!loading && !error && contacts.length === 0 && (
              <p>No contacts found.</p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: "#ddd", border: "none",
                  padding: "8px 16px", borderRadius: "4px", cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedContact || saving}
                style={{
                  backgroundColor: "#004aad", color: "white", border: "none",
                  padding: "8px 16px", borderRadius: "4px",
                  cursor: selectedContact && !saving ? "pointer" : "not-allowed",
                  opacity: selectedContact && !saving ? 1 : 0.6
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderLabel;